import React, { createContext, useContext, useReducer, useState } from "react";

import toast from "react-hot-toast";

import emailAlertReducer, { initialState, ACTION } from "./emailAlertReducer";
import Routes from "../../../../routes";

const EmailAlertContext = createContext();

const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
};

const {
    REFRESH_DATA,
    CHANGE_MODAL_STATE,
    UPDATE_OPTION,
    UPDATE_TABLE_DATA,
    RESET_DATA,
    UPDATE_BY_ACTION,
    UPDATE_MODAL_FORM,
    LOADING,
    SAVING
} = ACTION;

export const EmailAlertProvider = ({ children }) => {
    const [state, dispatch] = useReducer(emailAlertReducer, initialState);
    const [emailEdit, setEmailEdit] = useState("");

    const onRefreshData = () => {
        dispatch({
            type: REFRESH_DATA,
        });
    }

    const onChangeModalState = () => {
        dispatch({
            type: CHANGE_MODAL_STATE,
        });
    }

    const onUpdateOption = (options) => {
        dispatch({
            type: UPDATE_OPTION,
            payload: {
                options
            }
        });
    }

    const onUpdateTableData = (tableData) => {
        dispatch({
            type: UPDATE_TABLE_DATA,
            payload: {
                tableData
            }
        });
    }

    const onFormUpdate = (name, value) => {
        dispatch({
            type: UPDATE_MODAL_FORM,
            payload: {
                [name]: value
            }
        });
    }

    const onLoading = (loading) => {
        dispatch({
            type: LOADING,
            payload: {
                isLoading: loading
            }
        })
    }

    const onSaving = (saving) => {
        dispatch({
            type: SAVING,
            payload: {
                isSaving: saving
            }
        })
    }

    const onCloseModal = () => {
        dispatch({
            type: RESET_DATA,
        });
    }

    const onDeleteEmailAlert = async (emailAddress) => {
        const response = await fetch(Routes.EmailAlerts.DeleteByEmailAddress(), {
            method: 'POST',
            headers,
            body: JSON.stringify({
                emailAddress: emailAddress
            })
        });

        if (response.status === 201) {
            await fetchTableData();
            toast.success("The alert has been successfully deleted.");
        }
    }

    const onUpsertAlert = async () => {
        const normalizedEmail = state.emailAddress?.trim().toLowerCase();
        const isDuplicatedEmail = state.tableData.flat().some(array => array.emailAddress?.toLowerCase() === normalizedEmail);
        const isEditMode = state.modalTitle === "Edit";
        const isMatchingEmail = normalizedEmail === emailEdit?.toLowerCase();

        if (isDuplicatedEmail && (!isEditMode || !isMatchingEmail)) {
            onCloseModal();
            return toast.error("This email address is already in use. Please update the associated alert for this email address.");
        } 

        onSaving(true);
        const response = await fetch(Routes.EmailAlerts.Upsert(), {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                emailAddress: state.emailAddress,
                regions: state.selectedEmailAlert.map(x => ({
                    regionId: x.id,
                    regionName: x.name,
                    ...x
                }))
            })
        });

        if (response.status === 200) {
            switch (state.modalTitle) {
                case "Edit":
                    toast.success("The alert has been successfully edited.")
                    break;
                case "Duplicate":
                    toast.success("The alert has been successfully duplicated.")
                    break;
                default:
                    toast.success("The alert has been successfully created.")
                    break;
            }

            onRefreshData();
            onCloseModal();
            await fetchTableData();
        }
        onSaving(false);
    }

    const fetchEmailAlerts = async () => {
        const response = await fetch(Routes.EmailAlerts.GetByInstanceAssociation());
        if (response.status === 200) {
            const data = await response.json();
            onUpdateOption(data);
        }
    };

    const fetchTableData = async () => {
        const response = await fetch(Routes.EmailAlerts.GetTable());

        if (response.status === 200) {
            const data = await response.json();
            onUpdateTableData(data);
        }
    }

    const initializeEmailAlertData = async () => {
        onLoading(true);
        await Promise.all([fetchEmailAlerts(), fetchTableData()]);
        onLoading(false);
    }

    const onGetByEmailAddress = async (emailAddress, mode) => {

        const response = await fetch(Routes.EmailAlerts.GetByEmailAddress(emailAddress)).catch(() => null);
        setEmailEdit(emailAddress);

        if (response?.status === 200) {
            const data = await response.json();
            const selectedEmailAlert = data.regions.map(x => {
                return {
                    id: x.instanceAssociationId,
                    name: x.instanceAssociationName,
                    emailAlerts: x.emailAlerts.map(o => {
                        return {
                            id: o.emailAlertId,
                            name: o.emailAlertName
                        }
                    })
                }
            });

            dispatch({
                type: UPDATE_BY_ACTION,
                payload: {
                    selectedEmailAlert,
                    modalTitle: mode === "edit" ? "Edit" : "Duplicate",
                    emailAddress: mode === "edit" ? emailAddress : ""
                }
            });
            onChangeModalState();
        } else {
            toast.error("Unable to load the alerts for this email address. Please try again.");
        }
    }

    return (
        <EmailAlertContext.Provider
            value={{
                onRefreshData,
                onChangeModalState,
                onCloseModal,
                onDeleteEmailAlert,
                onUpsertAlert,
                fetchEmailAlerts,
                fetchTableData,
                initializeEmailAlertData,
                onGetByEmailAddress,
                onFormUpdate,
                openCreateEmail: state.openCreateEmail,
                emailAddress: state.emailAddress,
                selectedEmailAlert: state.selectedEmailAlert,
                dataRefresh: state.dataRefresh,
                options: state.options,
                tableData: state.tableData,
                modalTitle: state.modalTitle,
                isLoading: state.isLoading,
                isSaving: state.isSaving
            }}
        >
            {children}
        </EmailAlertContext.Provider>
    );
};

const useEmailAlert = () => {
    return useContext(EmailAlertContext);
};

export default useEmailAlert;
